// <k-wishlist id="wishlist"> is the single wishlist host rendered by the layout. Hearts anywhere
// fire command="--toggle" at it with data-wishlist-url; the header fires "--open". Saved product
// URLs live in localStorage; the drawer renders real product cards by fetching each product's
// "card" view (templates/product.card.liquid), so prices stay server-formatted and current.
const STORAGE_KEY = 'kanvora:wishlist';

class KWishlist extends HTMLElement {
  #ac;
  #items = [];
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.dialog = this.querySelector('dialog');
    this.list = this.querySelector('[data-wishlist-items]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.loading = this.querySelector('[data-wishlist-loading]');
    this.status = this.querySelector('[data-wishlist-status]');
    this.#items = this.#read();

    this.addEventListener('command', (event) => {
      if (event.command === '--toggle') this.#toggle(event.source);
      if (event.command === '--open') this.#open();
    }, opts);

    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      this.#items = this.#read();
      this.#sync();
    }, opts);

    // Cards arrive later through partial swaps, quick view and the drawer itself.
    this.observer = new MutationObserver(() => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#sync());
    });
    this.observer.observe(document.body, { childList: true, subtree: true });

    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.observer?.disconnect();
  }

  #normalize(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname;
    } catch {
      return null;
    }
  }

  #read() {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter((v) => typeof v === 'string') : [];
    } catch {
      return [];
    }
  }

  #write() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#items));
    } catch {
      // Storage can be unavailable in private modes; the in-memory list still works for this page.
    }
  }

  #toggle(source) {
    const url = this.#normalize(source?.dataset.wishlistUrl || '');
    if (!url) return;
    const index = this.#items.indexOf(url);
    const added = index === -1;
    if (added) this.#items.unshift(url);
    else this.#items.splice(index, 1);
    this.#write();
    this.#sync();
    this.#announce(added ? this.dataset.addedMessage : this.dataset.removedMessage);
    if (!added && this.dialog?.open) this.#render();
  }

  #sync() {
    const saved = new Set(this.#items);
    for (const button of document.querySelectorAll('[data-wishlist-url]')) {
      const url = this.#normalize(button.dataset.wishlistUrl);
      button.setAttribute('aria-pressed', String(saved.has(url)));
    }
    for (const badge of document.querySelectorAll('[data-wishlist-count]')) {
      badge.textContent = String(this.#items.length);
      badge.hidden = this.#items.length === 0;
    }
  }

  #announce(message) {
    if (!this.status || !message) return;
    this.status.textContent = '';
    requestAnimationFrame(() => {
      this.status.textContent = message;
    });
  }

  #open() {
    if (!this.dialog) return;
    this.#render();
    if (!this.dialog.open) this.dialog.showModal();
  }

  async #render() {
    if (!this.list) return;
    const urls = [...this.#items];
    this.empty.hidden = urls.length > 0;
    if (urls.length === 0) {
      this.list.replaceChildren();
      this.loading.hidden = true;
      return;
    }
    this.loading.hidden = false;
    const results = await Promise.all(urls.map(async (url) => {
      try {
        const response = await fetch(`${url}?view=card`, { headers: { Accept: 'text/html' } });
        if (response.status === 404) return { url, missing: true };
        if (!response.ok) return { url, html: '' };
        return { url, html: await response.text() };
      } catch {
        return { url, html: '' };
      }
    }));

    const missing = results.filter((r) => r.missing).map((r) => r.url);
    if (missing.length) {
      this.#items = this.#items.filter((u) => !missing.includes(u));
      this.#write();
    }

    const fragment = document.createDocumentFragment();
    for (const result of results) {
      if (!result.html) continue;
      const li = document.createElement('li');
      li.innerHTML = result.html;
      fragment.append(li);
    }
    this.list.replaceChildren(fragment);
    this.loading.hidden = true;
    this.empty.hidden = this.list.children.length > 0;
    this.#sync();
  }
}

if (!customElements.get('k-wishlist')) customElements.define('k-wishlist', KWishlist);
